import { TemplateElement, html, defineElement, when, unsafeHTML } from '@webtides/element-js';
import '../../../button/button.js';
import measureHeight from '../../../../utils/measure-height.js';
import style from './product-compare-table-item.css';

/**
 * @property {string} headline
 * @property {Object} link
 * @property {Object} image
 * @property {{column: string, type: string, value: string, image?: Object}[]} cells
 * @property {ProductCompareStore} productCompareStore
 */
export default class ProductCompareTableItem extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            headline: '',
            link: null,
            image: null,
            cells: [],
        };
    }

    injectProperties() {
        return {
            productCompareStore: (store) => {
                store.register(this);
            },
        };
    }

    disconnected() {
        this.productCompareStore?.unregister(this);
    }

    resetHeights() {
        if (this.$refs.header) this.$refs.header.style.minHeight = '';
        this.$refs.rows?.forEach((cell) => (cell.style.minHeight = ''));
    }

    getMeasurements() {
        return {
            headerHeight: measureHeight(this.$refs.header),
            rowHeights: (this.$refs.rows || []).map((cell, index) => ({ index, height: measureHeight(cell) })),
        };
    }

    applyHeights(headerHeight, rowHeights) {
        if (this.$refs.header) this.$refs.header.style.minHeight = `${headerHeight}px`;
        this.$refs.rows?.forEach((cell, index) => {
            if (rowHeights[index]) cell.style.minHeight = `${rowHeights[index]}px`;
        });
    }

    template() {
        return html`
            <div class="flex flex-col items-center px-8 pt-16 mb-fix-m" ref="header">
                ${when(
                    this.image?.src,
                    html`<img
                        loading="lazy"
                        src="${this.image.src}"
                        alt="${this.image?.alt || ''}"
                        class="size-[70px] object-contain mb-4"
                    />`,
                )}
                ${when(
                    this.headline,
                    html`<h3 class="w-full fix-body2-medium mb-fix-s text-center">${this.headline}</h3>`,
                )}
                ${when(
                    this.link?.url,
                    html`
                        <mb-button
                            class="mt-auto w-full @md/compare-table:w-auto"
                            href="${this.link?.url}"
                            variant="primary"
                            size="sm"
                            element="product-compare-table-item"
                        >
                            ${this.link?.text}
                        </mb-button>
                    `,
                )}
            </div>
            <div class="flex flex-col">
                ${this.cells.map(
                    (cell, index) => html`
                        <div
                            class="cell flex flex-col items-start justify-start @md/compare-table:items-center @md/compare-table:justify-center p-8 @md/compare-table:min-h-[64px] @md/compare-table:px-16 @md/compare-table:text-center border-b border-white text-green-medium ${index %
                                2 ===
                            0
                                ? 'bg-green-light25'
                                : ''}"
                            ref="rows[]"
                        >
                            <span class="w-full fix-label1-medium text-green-light @md/compare-table:hidden"
                                >${cell.column}</span
                            >
                            ${cell.type === 'checkmark'
                                ? html`<span class="flex items-center h-24 text-green-medium"
                                      >${cell.value === 'true'
                                          ? html`<mdx-icon-check size="sm"></mdx-icon-check>`
                                          : html`<span class="text-gray-30">–</span>`}</span
                                  >`
                                : html`
                                      <div
                                          class="w-full flex flex-col @md/compare-table:flex-row items-start @md/compare-table:items-center gap-8 @md/compare-table:justify-center"
                                      >
                                          ${when(
                                              cell.image?.src,
                                              html`<img
                                                  loading="lazy"
                                                  src="${cell.image?.src}"
                                                  alt="${cell.image?.alt || ''}"
                                                  class="w-[45px] aspect-[45/27] object-contain object-left @md/compare-table:object-center"
                                              />`,
                                          )}
                                          ${cell.type === 'richText' || cell.type === 'ratesValue'
                                              ? html`<span class="fix-body2 [&_p]:m-0">${unsafeHTML(cell.value)}</span>`
                                              : html`<span class="fix-body2">${cell.value}</span>`}
                                      </div>
                                  `}
                        </div>
                    `,
                )}
            </div>
        `;
    }
}

defineElement('mb-product-compare-table-item', ProductCompareTableItem);
